import {
  Fragment,
  Suspense,
  createContext,
  cloneElement,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactElement,
  type ReactNode,
} from 'react'
import { getCopy } from './resolve'
import { catalogueGate, setActiveLocale, subscribeTranslations, translationsVersion } from './registry'
import { splitMessage } from './format-message'
import { createFormatter, type Formatter } from './format'
import type { AppLocale, CatalogNode, CopyModule, ResolvedCopy } from './types'

/**
 * Whether the ACTIVE locale's catalogue actually arrived.
 *
 * D-L6 obligation 4: switching language needs a connection the first time, and
 * a UI that quietly renders English is how people conclude the setting is
 * broken. `'unavailable'` is a fact the switcher shows, not one it hides.
 *
 * `'ready'` is also the state for English, which is never fetched.
 */
export type CatalogueStatus = 'ready' | 'loading' | 'unavailable'

interface I18nContextValue {
  readonly locale: AppLocale
  readonly timeZone: string
  readonly direction: 'ltr' | 'rtl'
  readonly catalogueStatus: CatalogueStatus
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined)

export interface I18nProviderProps {
  locale: AppLocale
  /** The COMPANY's zone (M-03), not the browser's. */
  timeZone: string
  direction?: 'ltr' | 'rtl'
  /**
   * Fetches every `copy.<locale>.ts` for `locale` and registers it. Supplied by
   * the app because discovering the catalogues is a bundler concern
   * (`import.meta.glob`) and this package must stay buildable outside Vite —
   * `apps/api` imports its pure half for notification text.
   *
   * Not called for `'en'`: English is statically imported and precached, which
   * is the whole reason it can be the fallback.
   */
  loadCatalogues?: (locale: AppLocale) => Promise<void>
  /**
   * Shown while the first screen waits for its words (at most
   * `CATALOGUE_WAIT_MS`). It must contain NO text: it is shown precisely when
   * the text is not here yet. Nearer boundaries (routes, lazy panels) catch
   * a later wait first; this one is the net above everything that renders copy.
   */
  fallback?: ReactNode
  children: ReactNode
}

export function I18nProvider({ locale, timeZone, direction = 'ltr', loadCatalogues, fallback = null, children }: I18nProviderProps) {
  const [status, setStatus] = useState<CatalogueStatus>(locale === 'en' ? 'ready' : 'loading')

  // Mirrored out of React so `getCopy()` works in a mutation callback, a
  // `lib/` helper, and the query client's MutationCache — none of which have
  // hooks available.
  useEffect(() => {
    setActiveLocale(locale)
  }, [locale])

  useEffect(() => {
    if (locale === 'en' || !loadCatalogues) {
      setStatus('ready')
      return undefined
    }
    let active = true
    setStatus('loading')
    loadCatalogues(locale)
      .then(() => {
        if (active) setStatus('ready')
      })
      // The offline first-switch case. Reported, never thrown: the app must
      // keep working in English, and the switcher says why.
      .catch(() => {
        if (active) setStatus('unavailable')
      })
    return () => {
      active = false
    }
  }, [locale, loadCatalogues])

  const value = useMemo<I18nContextValue>(
    () => ({ locale, timeZone, direction, catalogueStatus: status }),
    [locale, timeZone, direction, status],
  )
  return (
    <I18nContext.Provider value={value}>
      <Suspense fallback={fallback}>{children}</Suspense>
    </I18nContext.Provider>
  )
}

export function useLocale(): I18nContextValue {
  const value = useContext(I18nContext)
  if (!value) throw new Error('useLocale must be used inside <I18nProvider>')
  return value
}

/**
 * The resolved catalogue for the active locale.
 *
 * Subscribes to the registry so the tree re-renders once the locale chunk
 * lands. `getCopy` returns a stable identity for the same
 * `(module, locale, version)`, so this is not a new object every render.
 *
 * **Waits for its own words** (the French split, 2026-09-25): each
 * `copy.<locale>.ts` is its own chunk, so a component whose catalogue has not
 * arrived suspends until it does — the screen appears in the right language
 * rather than flashing English — for at most `CATALOGUE_WAIT_MS`, after which
 * it renders English and re-renders when the words land. A catalogue that
 * cannot be fetched (offline, never cached) renders English at once.
 */
export function useCopy<T extends CatalogNode>(module: CopyModule<T>): ResolvedCopy<T> {
  const { locale } = useLocale()
  const version = useSyncExternalStore(subscribeTranslations, translationsVersion, translationsVersion)
  const copy = useMemo(() => getCopy(module, locale), [module, locale, version])
  const waiting = catalogueGate(module.namespace, locale)
  if (waiting) throw waiting
  return copy
}

export interface CopyProps {
  /** A message containing positional `<0>…</0>` slots. */
  of: string
  /**
   * One element per slot index. Each is cloned with the slot's text as its
   * children — so the element supplies the behaviour (a link, `<strong>`) and
   * the catalogue supplies every word, including the words inside the link.
   *
   * This is the reason concatenation is banned: `'Accept the ' + <a>terms</a>`
   * forces the translator to work with a fragment whose word order the sentence
   * may not allow in their language.
   */
  components: readonly ReactElement[]
}

export function Copy({ of, components }: CopyProps) {
  const parts = useMemo(() => splitMessage(of), [of])
  return (
    <>
      {parts.map((part, index) => {
        if (typeof part === 'string') return <Fragment key={index}>{part}</Fragment>
        const element = components[part.index]
        // An unmatched slot renders its TEXT, not nothing: a missing component
        // is a developer error, and losing the sentence is a worse symptom than
        // losing the link.
        if (!element) return <Fragment key={index}>{part.text}</Fragment>
        return <Fragment key={index}>{cloneElement(element, {}, part.text)}</Fragment>
      })}
    </>
  )
}

/**
 * Formatting pre-bound to BOTH the active locale and the COMPANY time zone.
 *
 * The pairing is the point. Threading only the locale is the regression this
 * whole layer exists to prevent, and it is silent — the date is well-formatted,
 * plausible, and a day wrong.
 *
 * The `Formatter` itself is built by `createFormatter` in the pure half, so the
 * Worker and non-React helpers can have one too; this hook is only the part
 * that reads the context and memoises.
 */
export function useFormat(): Formatter {
  const { locale, timeZone } = useLocale()
  return useMemo<Formatter>(() => createFormatter({ locale, timeZone }), [locale, timeZone])
}
